import { postsManifest } from '@/generated/posts-manifest'

export interface RouteMeta {
  path: string
  title: string
  description: string
  ogType?: 'website' | 'article'
  // Set only for blog posts - drives BlogPosting/article JSON-LD and OG article tags.
  post?: (typeof postsManifest)[number]
}

export const SITE_NAME = 'CloudSoft'
export const SITE_URL = 'https://www.cloudsoft.com.pl'
export const DEFAULT_DESCRIPTION =
  'Cloud architecture, serverless, migrations and scalable backends - expert help for small teams, by Marcin Malinowski.'

const STATIC_META: Record<string, RouteMeta> = {
  '/': {
    path: '/',
    title: 'CloudSoft - Independent Cloud Consulting',
    description: DEFAULT_DESCRIPTION,
    ogType: 'website'
  },
  '/about': {
    path: '/about',
    title: 'About Marcin Malinowski - CloudSoft',
    description:
      'Backend and cloud developer since 2007, working across telecom, insurance and automotive to build scalable, low-latency software.'
  },
  '/company': {
    path: '/company',
    title: 'About CloudSoft - Cloud Consulting & Backend Engineering',
    description:
      'CloudSoft Marcin Malinowski delivers pragmatic, maintainable cloud architectures and backend solutions that help small teams and startups scale reliably on AWS.'
  },
  '/contact': {
    path: '/contact',
    title: 'Contact CloudSoft - Cloud Consulting',
    description:
      'Get in touch with CloudSoft to discuss cloud architecture, backend engineering and AWS cost optimization projects.'
  },
  '/cookie-policy': {
    path: '/cookie-policy',
    title: 'Cookie Policy - CloudSoft',
    description: 'Details on the cookies CloudSoft uses and how to manage your preferences.'
  },
  '/portfolio': {
    path: '/portfolio',
    title: 'Portfolio - CloudSoft',
    description:
      'Cloud and backend engineering portfolio spanning industrial, maritime, automotive, fintech, telecom and public sector projects.'
  },
  '/services': {
    path: '/services',
    title: 'Services - CloudSoft',
    description:
      'Cloud architecture, serverless applications, Infrastructure as Code and backend engineering services for startups and small teams.'
  },
  '/support': {
    path: '/support',
    title: 'Support & Contact - CloudSoft',
    description: 'Support availability, response times and contact channels for CloudSoft consulting engagements.'
  },
  '/terms-of-service': {
    path: '/terms-of-service',
    title: 'Terms of Service - CloudSoft',
    description: 'Terms of Service governing consulting and development engagements with CloudSoft Marcin Malinowski.'
  },
  '/privacy-policy/v1': {
    path: '/privacy-policy/v1',
    title: 'Privacy Policy - CloudSoft',
    description: 'How CloudSoft Marcin Malinowski collects, uses and protects your personal data.'
  },
  '/posts': {
    path: '/posts',
    title: 'Blog - CloudSoft',
    description: 'AWS, cloud architecture and backend engineering articles by Marcin Malinowski.',
    ogType: 'website'
  }
}

function postMeta(slug: string): RouteMeta | undefined {
  const post = postsManifest.find(candidate => candidate.slug === slug)
  if (!post) {
    return undefined
  }
  return {
    path: `/posts/${slug}`,
    title: `${post.data.title} - CloudSoft`,
    description: post.data.excerpt,
    ogType: 'article',
    post
  }
}

export function resolveMeta(path: string): RouteMeta {
  const staticEntry = STATIC_META[path]
  if (staticEntry) {
    return staticEntry
  }

  const postSlugMatch = path.match(/^\/posts\/([^/]+)$/)
  if (postSlugMatch) {
    const meta = postMeta(postSlugMatch[1])
    if (meta) {
      return meta
    }
  }

  return {
    path,
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION
  }
}
